import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Tag as TagIcon, Trash2, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { cn, normalize } from '../lib/utils';
import { Spinner, useToast } from './Feedback';
import type { Tag } from '../types';

/**
 * Éditeur d'étiquettes d'une application.
 *
 * Deux besoins se croisent ici : poser sur l'application une étiquette qui
 * existe déjà ailleurs dans le catalogue, et en créer une nouvelle. Le champ
 * unique sert les deux : ce qu'on tape filtre la liste venue de la base, et si
 * rien ne correspond, la création est proposée sur place.
 *
 * Le décompte affiché à côté de chaque étiquette vient de la base et compte les
 * applications vivantes. C'est lui qui dissuade de créer « Finance » quand
 * « Comptabilité » sert déjà à six applications.
 */

interface TagPickerProps {
    value: string[];
    onChange: (tags: string[]) => void;
}

/** Deux libellés qui ne diffèrent que par la casse ou les accents font doublon. */
function sameTag(a: string, b: string): boolean {
    return normalize(a.trim()) === normalize(b.trim());
}

export function TagPicker({ value, onChange }: TagPickerProps) {
    const [search, setSearch] = useState('');
    const queryClient = useQueryClient();
    const { notify } = useToast();

    const { data: tags = [], isLoading } = useQuery({
        queryKey: ['admin-tags'],
        // La liste d'administration porte aussi les étiquettes qu'aucune
        // application ne tient encore : celle qu'on vient de créer en fait partie.
        queryFn: () => api.get<Tag[]>('/api/admin/tags'),
    });

    const trimmed = search.trim();

    const suggestions = useMemo(() => {
        const needle = normalize(trimmed);
        const matching =
            needle === '' ? tags : tags.filter((tag) => normalize(tag.name).includes(needle));

        // Les plus employées d'abord : ce sont celles que le catalogue attend.
        return [...matching].sort(
            (a, b) => b.usageCount - a.usageCount || a.name.localeCompare(b.name)
        );
    }, [tags, trimmed]);

    const existing = tags.find((tag) => sameTag(tag.name, trimmed));
    const alreadySelected = value.some((tag) => sameTag(tag, trimmed));
    const canCreate = trimmed !== '' && existing === undefined && !alreadySelected;

    const add = (name: string) => {
        if (!value.some((tag) => sameTag(tag, name))) onChange([...value, name]);
        setSearch('');
    };

    const create = useMutation({
        mutationFn: (name: string) => api.post<Tag>('/api/admin/tags', { name }),
        onSuccess: (tag) => {
            void queryClient.invalidateQueries({ queryKey: ['admin-tags'] });
            add(tag.name);

            // L'API renvoie l'étiquette existante quand le nom saisi retombe sur
            // son slug : « R.H. » et « RH » sont la même. Annoncer une création
            // dans ce cas ferait croire à un doublon.
            const reused = tags.some((item) => item.id === tag.id);
            notify(
                reused
                    ? `Étiquette « ${tag.name} » déjà au catalogue, elle a été reprise.`
                    : `Étiquette « ${tag.name} » créée.`,
                reused ? 'info' : 'success'
            );
        },
        onError: (error) =>
            notify(
                error instanceof ApiError ? error.message : 'La création a échoué.',
                'error'
            ),
    });

    const remove = useMutation({
        mutationFn: (tag: Tag) => api.delete(`/api/admin/tags/${tag.id}`),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['admin-tags'] });
            notify('Étiquette supprimée.', 'success');
        },
        onError: (error) =>
            notify(
                error instanceof ApiError ? error.message : 'La suppression a échoué.',
                'error'
            ),
    });

    return (
        <div className="space-y-2.5">
            {value.length > 0 && (
                <ul className="flex flex-wrap gap-1.5" aria-label="Étiquettes de cette application">
                    {value.map((tag) => (
                        <li key={tag}>
                            <span className="badge badge-neutral gap-1 pr-1">
                                {tag}
                                <button
                                    type="button"
                                    onClick={() => onChange(value.filter((item) => item !== tag))}
                                    className="rounded-full p-0.5 hover:bg-[var(--color-border)]"
                                    aria-label={`Retirer l’étiquette ${tag}`}
                                >
                                    <X className="w-3 h-3" aria-hidden />
                                </button>
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            <div className="flex gap-2">
                <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key !== 'Enter') return;
                        // Sans cela la touche Entrée validerait le formulaire de
                        // l'éditeur, alors qu'ici elle pose l'étiquette saisie.
                        event.preventDefault();
                        if (trimmed === '') return;
                        // On pose le libellé du catalogue, pas celui qui vient d'être
                        // tapé : « finance » saisi ne doit pas renommer « Finance ».
                        if (existing) add(existing.name);
                        else if (canCreate) create.mutate(trimmed);
                    }}
                    className="input"
                    placeholder="Chercher une étiquette, ou en saisir une nouvelle"
                    maxLength={64}
                    aria-label="Chercher ou créer une étiquette"
                />
                <button
                    type="button"
                    onClick={() => create.mutate(trimmed)}
                    disabled={!canCreate || create.isPending}
                    className="btn btn-secondary shrink-0"
                >
                    {create.isPending ? (
                        <Spinner className="w-4 h-4" />
                    ) : (
                        <Plus className="w-4 h-4" aria-hidden />
                    )}
                    Créer
                </button>
            </div>

            <div className="border border-[var(--color-border)] rounded-lg max-h-44 overflow-y-auto">
                {isLoading ? (
                    <p className="px-3 py-4 text-xs text-[var(--color-text-secondary)]">
                        Chargement des étiquettes…
                    </p>
                ) : suggestions.length === 0 ? (
                    <p className="px-3 py-4 text-xs text-[var(--color-text-secondary)]">
                        {tags.length === 0
                            ? 'Aucune étiquette dans le catalogue pour l’instant. Saisissez la première ci-dessus.'
                            : 'Aucune étiquette existante ne correspond. Le bouton « Créer » l’ajoute au catalogue.'}
                    </p>
                ) : (
                    <ul>
                        {suggestions.map((tag) => {
                            const selected = value.some((item) => sameTag(item, tag.name));

                            return (
                                <li
                                    key={tag.id}
                                    className="flex items-center gap-2 px-2 py-1.5 border-b border-[var(--color-border)] last:border-b-0"
                                >
                                    <button
                                        type="button"
                                        onClick={() =>
                                            selected
                                                ? onChange(
                                                      value.filter(
                                                          (item) => !sameTag(item, tag.name)
                                                      )
                                                  )
                                                : add(tag.name)
                                        }
                                        className={cn(
                                            'flex flex-1 min-w-0 items-center gap-2 rounded px-1 py-0.5 text-left text-sm',
                                            selected
                                                ? 'text-[var(--color-primary)] font-medium'
                                                : 'text-[var(--color-text)] hover:text-[var(--color-primary)]'
                                        )}
                                        aria-pressed={selected}
                                    >
                                        <TagIcon
                                            className="w-3.5 h-3.5 shrink-0 text-[var(--color-text-muted)]"
                                            aria-hidden
                                        />
                                        <span className="truncate">{tag.name}</span>
                                    </button>

                                    <span className="shrink-0 text-xs tabular-nums text-[var(--color-text-muted)]">
                                        {tag.usageCount === 0
                                            ? 'jamais posée'
                                            : `${tag.usageCount} application${tag.usageCount > 1 ? 's' : ''}`}
                                    </span>

                                    {/* Une étiquette que rien ne porte peut être effacée : c'est
                                        le seul recours après une faute de frappe à la création. */}
                                    {tag.usageCount === 0 && !selected && (
                                        <button
                                            type="button"
                                            onClick={() => remove.mutate(tag)}
                                            disabled={remove.isPending}
                                            className="btn btn-ghost shrink-0 p-1 text-[var(--color-error)]"
                                            aria-label={`Supprimer l’étiquette ${tag.name}`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" aria-hidden />
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
